import React, { useState, useEffect, useContext, useCallback, } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { FetchContext } from 'src/context/FetchContext';
import { AuthContext } from 'src/context/AuthContext';
import { API_ROUTES } from 'src/services/constant';
import { getTodoDraftColor } from 'src/utils/scheduleColorUtils';
import CommentIcon from '@mui/icons-material/Comment';
import CommentsModal from 'src/components/SharedComponent/Scheduling/comment_modal';
import { Autocomplete, TextField } from '@mui/material';

import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';


function AssignUserModel({
  opentStatus,
  setUserModal,
  employees,
  schedule,
  handleUserAssign,
}) {
  const [openCommentModal, setCommentModal] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const { authAxios } = useContext(FetchContext);
  const { state } = useContext(AuthContext);
  const isAdmin = state.user.role === 'admin';
  const { PRIVATE_ROUTE } = API_ROUTES;
  const [costItems, setCostItems] = useState([]);
  const URGENCY_OPTIONS = [
    'Normal',
    'Urgent',
  ];

  const STATUS_OPTIONS = [
    'Open',
    'Ready For Drafting',
    'Assigned',
    'Ready For Review',
    'Review In Progress',
    'Review Complete',
    ...(isAdmin ? ['Closed'] : []),
  ];
  const [formData, setFormData] = useState({

    employee:
      [
        'ready for review',
        'review in progress',
        'review complete',
      ].includes(schedule.status?.toLowerCase())
        ? (
          schedule.checked_manager?.[0]?._id ||
          schedule.project_manager?._id ||
          ''
        )
        : (
          schedule.assigned_members?.[0]?._id ||
          ''
        ),

    status:

      schedule.status === 'assigned'
        ? 'Assigned'
        : schedule.status === 'open'
          ? 'Open'
          : schedule.status === 'ready for drafting'
            ? 'Ready For Drafting'
            : schedule.status === 'ready for review'
              ? 'Ready For Review'
              : schedule.status === 'review in progress'
                ? 'Review In Progress'
                : schedule.status === 'review complete'
                  ? 'Review Complete'
                  : schedule.status === 'completed'
                    ? (isAdmin ? 'Closed' : 'Completed')
                    : schedule.status || '',
    urgency:
      schedule.urgency === 'Urgent'
        ? 'Urgent'
        : 'Normal',
    fieldTripDate:
      schedule.field_trip_date
        ? dayjs(schedule.field_trip_date.split('T')[0])
        : null,

    taskScope:
      schedule.task_scope_id?.name || '',

    costItem:
      Array.isArray(schedule.cost_item)
        ? schedule.cost_item[0]
        : schedule.cost_item || '',

    estimatedHours:
      schedule.estimated_hours || '',

    description:
      schedule.description || '',
  });

  const [errors, setErrors] = useState({});

  const fetchCommentCount = useCallback(async () => {
    if (!schedule?._id) return;

    try {
      const { data } = await authAxios.get(
        `${PRIVATE_ROUTE.FETCH_ALL_COMMENTS}?schedule_id=${schedule._id}`
      );

      if (data?.statusCode === 200) {
        setCommentCount(data.data?.length || 0);
      }
    } catch (err) {
      console.error(err);
    }
  }, [authAxios, PRIVATE_ROUTE.FETCH_ALL_COMMENTS, schedule?._id]);

  useEffect(() => {
    fetchCommentCount();
  }, [fetchCommentCount]);

  useEffect(() => {
    const getCostItems = async () => {
      try {
        const response = await authAxios.get(
          PRIVATE_ROUTE.GET_DRAFT_COST_ITEMS
        );

        setCostItems(response.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    getCostItems();
  }, [authAxios, PRIVATE_ROUTE.GET_DRAFT_COST_ITEMS]);

  const handleChange = (field, value) => {
    if (field === 'status') {
      const reviewStatuses = [
        'Ready For Review',
        'Review In Progress',
        'Review Complete',
      ];

      let { employee } = formData;

      if (reviewStatuses.includes(value)) {
        employee =
          schedule.checked_manager?.[0]?._id ||
          schedule.project_manager?._id ||
          employee;
      }

      setFormData((prev) => ({
        ...prev,
        status: value,
        employee,
      }));

      if (errors.status || errors.employee) {
        setErrors((prev) => ({
          ...prev,
          status: undefined,
          employee: undefined,
        }));
      }

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validate = () => {

    const newErrors = {};
    const status = formData.status?.toLowerCase();
    const selectedEmployee = employees.find(
      (emp) => emp.id === formData.employee
    );

    // Employee is required for all statuses except Open and Ready For Drafting
    if (
      status !== 'open' &&
      status !== 'ready for drafting' &&
      !formData.employee
    ) {
      newErrors.employee = 'Employee is required';
    }

    // Employee should not be selected for Open or Ready For Drafting
    if (
      formData.employee &&
      (status === 'open' || status === 'ready for drafting')
    ) {
      newErrors.employee =
        'Remove the employee or change the status to Assigned.';
    }

    // Only managers/admins can be assigned these statuses
    const managerOnlyStatuses = [
      'ready for review',
      'review in progress',
      'review complete',
      'completed',
    ];

    if (
      formData.employee &&
      managerOnlyStatuses.includes(status) &&
      selectedEmployee &&
      !['manager', 'admin'].includes(selectedEmployee.role?.toLowerCase())
    ) {
      newErrors.employee =
        'Only managers can be assigned when the status is Ready For Review, Review In Progress or Review Complete';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    if (status !== 'open' && !formData.fieldTripDate) {
      newErrors.fieldTripDate = 'Field Trip Date is required';
    }

    if (!formData.costItem) {
      newErrors.costItem = 'Cost Item is required';
    }

    if (!formData.estimatedHours) {
      newErrors.estimatedHours = 'Estimated Hours is required';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;

  };

  const saveAssignment = async () => {
    const employeeId = formData.employee;

    const status = formData.status
      ? (
        formData.status === 'Closed'
          ? 'completed'
          : formData.status.toLowerCase()
      )
      : null;

    const reviewStatuses = [
      'ready for review',
      'review in progress',
      'review complete',
      'completed',
    ];

    const payload = {      
      assigned_members: reviewStatuses.includes(status)
        ? schedule.assigned_members?.map((m) => m._id) || []
        : employeeId
          ? [employeeId]
          : [],
      ...(reviewStatuses.includes(status) && {
        checked_manager: employeeId ? [employeeId] : [],
      }),
      status,
      urgency:
        status === 'open'
          ? ''
          : formData.urgency || 'Normal',
      field_trip_date: formData.fieldTripDate
        ? formData.fieldTripDate.format('YYYY-MM-DD')
        : null,
      ...(status === 'assigned' && {
        assigned_date: new Date().toISOString(),
      }),
      ...(status === 'ready for review' && {
        ready_for_review_date: new Date().toISOString(),
      }),
      task_scope_id: schedule.task_scope_id?._id,
      cost_item: formData.costItem,
      estimated_hours: formData.estimatedHours,
      description: formData.description,
      updatedAt: schedule.updatedAt,
    };

    setUserModal(false);    
    await handleUserAssign(schedule._id, payload);
  };

  const fieldLabel = {
    fontSize: '.8rem',
    fontWeight: 600,
    color: '#555',
    mb: .5,
  };

  return (

    <Modal open={opentStatus}>

      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: {
            xs: '95%',
            sm: 700,
          },
          maxHeight: '90vh',
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',

          display: 'flex',
          flexDirection: 'column',
        }}
      >

        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PeopleAltIcon sx={{ color: '#fff', fontSize: 22 }} />
            <Box>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}
              >
                TO DO DRAFT
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                Assign User
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                {schedule.job_id?.number_str && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: getTodoDraftColor(schedule.urgency),
                      borderRadius: '20px',
                      px: 1.25,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.75)',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: 0.8,
                      }}
                    >
                      JOB
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 800 }}>
                      {schedule.job_id.number_str}
                    </Typography>
                  </Box>
                )}
                {schedule.task?.number_str && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      borderRadius: '20px',
                      px: 1.25,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.75)',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        letterSpacing: 0.8,
                      }}
                    >
                      TASK
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 800 }}>
                      {schedule.task.number_str}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={() => setUserModal(false)} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* Body */}
        <Box
          sx={{
            p: 3,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 2.5,
          }}
        >
          <Grid container spacing={2}>
            <>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...fieldLabel,
                    color: errors.employee ? '#d32f2f' : '#555',
                  }}
                >
                  Employee/Manager
                </Typography>

                <Autocomplete
                  options={employees}
                  value={
                    employees.find((emp) => emp.id === formData.employee) || null
                  }
                  onChange={(_, value) =>
                    handleChange('employee', value ? value.id : '')
                  }
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  clearOnEscape
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search employee..."
                      error={!!errors.employee}
                      helperText={errors.employee}
                    />
                  )}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    ...fieldLabel,
                    color: errors.status ? '#d32f2f' : '#555',
                  }}
                >
                  Status
                </Typography>

                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.status}
                >
                  <Select
                    value={formData.status}
                    onChange={({ target: { value } }) =>
                      handleChange('status', value)
                    }
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem
                        key={status}
                        value={status}
                      >
                        {status}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>
                    {errors.status}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Urgency */}
              <Grid item xs={12} sm={6}>
                <Typography sx={fieldLabel}>
                  Urgency
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={formData.urgency}
                    onChange={(e) => handleChange('urgency', e.target.value)}
                  >
                    {URGENCY_OPTIONS.map((urgency) => (
                      <MenuItem key={urgency} value={urgency}>
                        {urgency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Field Trip Date */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...fieldLabel,
                    color: errors.fieldTripDate ? '#d32f2f' : '#555',
                  }}
                >
                  Field Date/Requested
                </Typography>

                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                >
                  <DatePicker
                    value={formData.fieldTripDate}
                    onChange={(value) =>
                      handleChange('fieldTripDate', value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={!!errors.fieldTripDate}
                        helperText={errors.fieldTripDate}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Task Scope */}
              <Grid item xs={12}>
                <Typography sx={fieldLabel}>
                  Task Scope
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  value={formData.taskScope}
                  disabled
                />
              </Grid>

              {/* Cost Item */}
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    ...fieldLabel,
                    color: errors.costItem ? '#d32f2f' : '#555',
                  }}
                >
                  Cost Item
                </Typography>

                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.costItem}
                >
                  <Select
                    value={formData.costItem}
                    onChange={(e) =>
                      handleChange('costItem', e.target.value)
                    }
                  >
                    {costItems.map((item) => (
                      <MenuItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>
                    {errors.costItem}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Estimated Hours */}
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    ...fieldLabel,
                    color: errors.estimatedHours
                      ? '#d32f2f'
                      : '#555',
                  }}
                >
                  Estimated Hours
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{
                    min: 0,
                    step: 0.5,
                  }}
                  value={formData.estimatedHours}
                  onChange={(e) =>
                    handleChange(
                      'estimatedHours',
                      e.target.value
                    )
                  }
                  error={!!errors.estimatedHours}
                  helperText={errors.estimatedHours}
                />
              </Grid>
              {/* Description */}
              <Grid item xs={12}>
                <Typography sx={fieldLabel}>
                  Description
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={formData.description}
                  onChange={(e) =>
                    handleChange('description', e.target.value)
                  }
                />
              </Grid>
            </>

          </Grid>
        </Box>
        {openCommentModal && (
          <CommentsModal
            opentStatus={openCommentModal}
            setCommentModal={setCommentModal}
            selectedSchedule={schedule}
            user={state.user}
            onCommentAdded={() => setCommentCount((c) => c + 1)}
          />
        )}
        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderTop: `1px solid ${TEAL_LIGHT}`,
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          {/* Left */}
          <Button
            size="small"
            variant={commentCount > 0 ? 'contained' : 'text'}
            startIcon={<CommentIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => setCommentModal(true)}
            sx={
              commentCount > 0
                ? {
                  backgroundColor: '#65C8D0',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: '#3DADB5' },
                }
                : {
                  color: '#3DADB5',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': { backgroundColor: '#E0F6F8' },
                }
            }
          >
            Comments{commentCount > 0 ? ` (${commentCount})` : ''}
          </Button>

          {/* Right */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setUserModal(false)}
              sx={{
                borderColor: TEAL_DARK,
                color: TEAL_DARK,
                '&:hover': {
                  borderColor: TEAL_DARK,
                  backgroundColor: TEAL_LIGHT,
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                if (validate()) {
                  saveAssignment();
                }
              }}
              sx={{
                backgroundColor: TEAL_DARK,
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  backgroundColor: TEAL,
                },
              }}
            >
              Save
            </Button>
          </Stack>
        </Box>

      </Box>
    </Modal>
  );
}
AssignUserModel.propTypes = {
  opentStatus: PropTypes.bool.isRequired,
  setUserModal: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
    })
  ).isRequired,

  schedule: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    updatedAt: PropTypes.string,
    status: PropTypes.string,
    urgency: PropTypes.string,

    field_trip_date: PropTypes.string,
    planned_date: PropTypes.string,

    estimated_hours: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    checked_manager: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),

    description: PropTypes.string,

    assigned_members: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),

    cost_item: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),

    task_scope_id: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),

    job_id: PropTypes.shape({
      number_str: PropTypes.string,
    }),

    task: PropTypes.shape({
      number_str: PropTypes.string,
    }),

    project_manager: PropTypes.shape({
      _id: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    }),

    comments: PropTypes.array,
  }).isRequired,

  handleUserAssign: PropTypes.func.isRequired,
};



export default AssignUserModel;

